import { useCallback, useMemo, useRef, useState } from 'react';
import type { CellClassParams, CellValueChangedEvent, RowClassParams, RowStyle } from 'ag-grid-community';

/**
 * 편집 그리드(CRUD) 표준 — 조회·추가·수정·삭제를 그리드 하나에서 (2026-11-03).
 * 규칙은 .claude/skills/fdsapp-ui/SKILL.md §14. 쓰는 곳: 계좌·전략 관리(AccountAdmin), 전략 슬롯(StrategySlotManager).
 *
 *   [행 추가]  → 맨 위에 새 행(초록). id 는 음수 임시값 — 저장 때 서버가 진짜 id 를 준다
 *   칸 수정    → 노란 칸. 값은 rowData 가 아니라 이 훅에 둔다(필터·재조회로 rowData 가 다시 만들어져도 남게)
 *   [변경 저장] → 새 행 등록 + 고친 행 수정을 한 번에. 실패한 행만 남는다
 *   [되돌리기]  → 저장 안 한 추가·수정을 전부 버린다
 *   [삭제]     → 체크한 행. 새 행은 그냥 지우고, 저장된 행은 화면마다 정한다(물리 삭제 · 정지)
 */
export function useGridEdits<T>(idOf: (r: T) => number) {
  const [edits, setEdits] = useState<Record<number, Partial<T>>>({});
  const [added, setAdded] = useState<T[]>([]);
  const seq = useRef(0);

  const onCellValueChanged = useCallback((e: CellValueChangedEvent<T>) => {
    const f = e.colDef.field as string | undefined;
    if (!e.data || !f || e.oldValue === e.newValue) return;
    const id = idOf(e.data);
    setEdits(p => ({ ...p, [id]: { ...p[id], [f]: e.newValue } as Partial<T> }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 새 행 — make 는 임시 id(음수)를 받아 기본값을 채운 행을 돌려준다 */
  const add = useCallback((make: (tmpId: number) => T) => {
    seq.current -= 1;
    const row = make(seq.current);
    setAdded(p => [row, ...p]);
    return row;
  }, []);

  /** 새 행 + 서버 행에 고친 값을 덮어 화면용 rowData 를 만든다 */
  const apply = useCallback((rows: T[]) => [...added, ...rows].map(r => {
    const x = edits[idOf(r)];
    return x ? { ...r, ...x } : r;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [edits, added]);

  /** defaultColDef 에 넣는다 — 고친 칸을 노랗게 (App.css .cell-edited) */
  const cellClassRules = useMemo(() => ({
    'cell-edited': (p: CellClassParams<T>) => {
      const f = p.colDef.field as string | undefined;
      if (!p.data || !f) return false;
      const x = edits[idOf(p.data)];
      return !!x && f in (x as object);
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [edits]);

  /** getRowStyle 에 넣는다 — 새 행 초록 */
  const newRowStyle = useCallback((p: RowClassParams<T>): RowStyle | undefined =>
    p.data && idOf(p.data) < 0 ? { backgroundColor: '#f0fdf4' } : undefined,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  /** 저장했거나 지운 행을 편집 상태에서 뺀다 (새 행이면 목록에서도) */
  const drop = useCallback((ids: number[]) => {
    setEdits(p => { const n = { ...p }; ids.forEach(id => delete n[id]); return n; });
    setAdded(p => p.filter(r => !ids.includes(idOf(r))));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => { setEdits({}); setAdded([]); }, []);

  /** 저장할 것: 새 행(고친 값 합침) · 고친 기존 행 id */
  const pending = useMemo(() => ({
    added: added.map(r => ({ ...r, ...(edits[idOf(r)] ?? {}) }) as T),
    changed: Object.keys(edits).map(Number).filter(id => id > 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [added, edits]);

  const count = pending.added.length + pending.changed.length;
  return { edits, added, count, pending, onCellValueChanged, add, apply, cellClassRules, newRowStyle, drop, reset };
}
